import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { CARS, getCarById } from '../entities/cars.js';
import { spawnable } from '../entities/characters.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, getEquippedCar } from '../core/save.js';
import { Race, TrackSpline } from '../minigames/race.js';

// Toast Town Speedway — top-down toy-diorama racer reached through the Garage.
// States: explore (walk the paddock) → playing (race) → results.

const TRACK_WIDTH = 14;
const KERB_WIDTH = 1.4;

export function createSpeedwayScene(){
  return () => new SpeedwayScene();
}

class SpeedwayScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x87CE78);

    // top-down orthographic camera
    const aspect = window.innerWidth / window.innerHeight;
    const viewSize = 62;
    this.camera = new THREE.OrthographicCamera(
      -viewSize * aspect / 2, viewSize * aspect / 2,
      viewSize / 2, -viewSize / 2,
      0.1, 200
    );
    this.camera.position.set(0, 70, 0);
    this.camera.lookAt(0, 0, 0);
    this.camera.rotation.z = -0.15; // slight toy-diorama tilt

    this._buildLights();
    this.spline = this._buildTrack();
    this._buildScenery();

    this.bread = new BreadBoi();
    this.bread.root.position.set(-8, 0, 22);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.race = null;
    this.carMeshes = [];

    this.hotspots = [
      { pos: new THREE.Vector3(8, 0, 22), r: 3.2, label: '🏁 race', action: () => this._startRace() },
      { pos: new THREE.Vector3(-18, 0, 26), r: 2.8, label: '← back to Garage', action: () => this.manager.goTo('garage', { from: 'speedway' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.78));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.85);
    sun.position.set(10, 30, 8); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 60;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildTrack(){
    // an irregular loop: a long straight, a tight hairpin, a sweeping back,
    // and a fast chicane onto the front straight
    const pts = [
      new THREE.Vector3(0, 0, 28),
      new THREE.Vector3(18, 0, 22),
      new THREE.Vector3(26, 0, 8),
      new THREE.Vector3(22, 0, -10),
      new THREE.Vector3(8, 0, -24),
      new THREE.Vector3(-10, 0, -26),
      new THREE.Vector3(-24, 0, -14),
      new THREE.Vector3(-28, 0, 4),
      new THREE.Vector3(-20, 0, 18),
      new THREE.Vector3(-8, 0, 26)
    ];
    const spline = new TrackSpline(pts);

    // asphalt ribbon
    const segs = 120;
    const stripPts = [];
    for (let i = 0; i <= segs; i++){
      const s = i / segs;
      const p = spline.getPoint(s);
      const t = spline.getTangent(s);
      const n = new THREE.Vector3(-t.z, 0, t.x).normalize();
      stripPts.push(p.clone().addScaledVector(n, TRACK_WIDTH / 2));
      stripPts.push(p.clone().addScaledVector(n, -TRACK_WIDTH / 2));
    }
    const ribbonGeo = new THREE.BufferGeometry().setFromPoints(stripPts);
    const ribbonPos = ribbonGeo.attributes.position.array;
    const indices = [];
    for (let i = 0; i < segs; i++){
      const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
      indices.push(a, c, b, b, c, d);
    }
    ribbonGeo.setIndex(indices);
    ribbonGeo.computeVertexNormals();
    const ribbon = new THREE.Mesh(ribbonGeo, new THREE.MeshLambertMaterial({ color: 0x5B6B72 }));
    ribbon.rotation.x = 0; ribbon.receiveShadow = true; ribbon.position.y = 0.02;
    this.scene.add(ribbon);

    // red/white kerbs
    const kerbPts = [];
    for (let i = 0; i <= segs * 2; i++){
      const s = i / (segs * 2);
      const p = spline.getPoint(s);
      const t = spline.getTangent(s);
      const n = new THREE.Vector3(-t.z, 0, t.x).normalize();
      const w = TRACK_WIDTH / 2 + KERB_WIDTH / 2;
      const side = (i % 2 === 0) ? 1 : -1;
      const col = (Math.floor(i / 4) % 2 === 0) ? 0xE23B4E : 0xFFFFFF;
      const k = new THREE.Mesh(
        new THREE.BoxGeometry(KERB_WIDTH, 0.12, 1.2),
        new THREE.MeshLambertMaterial({ color: col })
      );
      k.position.copy(p).addScaledVector(n, side * w);
      k.lookAt(p.clone().add(t));
      k.receiveShadow = true;
      this.scene.add(k);
    }

    // grass infield + apron disc
    const grass = new THREE.Mesh(
      new THREE.CircleGeometry(48, 64),
      new THREE.MeshLambertMaterial({ color: C.grass })
    );
    grass.rotation.x = -Math.PI / 2; grass.position.y = -0.02; grass.receiveShadow = true;
    this.scene.add(grass);

    // start/finish line
    const startP = spline.getPoint(0);
    const startT = spline.getTangent(0);
    const startN = new THREE.Vector3(-startT.z, 0, startT.x).normalize();
    const line = new THREE.Mesh(
      new THREE.PlaneGeometry(TRACK_WIDTH, 1.4),
      new THREE.MeshBasicMaterial({ color: 0xFFFFFF })
    );
    line.rotation.x = -Math.PI / 2;
    line.position.copy(startP).add(new THREE.Vector3(0, 0.04, 0));
    line.lookAt(startP.clone().add(startT));
    this.scene.add(line);
    const checkers = [];
    for (let i = 0; i < 8; i++){
      const c = new THREE.Mesh(
        new THREE.PlaneGeometry(TRACK_WIDTH / 8, 1.4),
        new THREE.MeshBasicMaterial({ color: i % 2 === 0 ? 0x111111 : 0xFFFFFF })
      );
      c.rotation.x = -Math.PI / 2;
      const offset = (i - 3.5) * (TRACK_WIDTH / 8);
      c.position.copy(startP).addScaledVector(startN, offset).add(new THREE.Vector3(0, 0.045, 0));
      c.lookAt(startP.clone().add(startT));
      this.scene.add(c);
    }

    // pit building
    const pit = new THREE.Group();
    pit.position.set(-16, 0, 22);
    box(10, 4, 6, 0xD9A441, 0, 2, 0, pit);
    box(10.6, 0.6, 6.6, 0xA67B5B, 0, 4.3, 0, pit);
    const pitsign = textSprite('Pretzel Pit Stop', 30, '#6E4526', 5, 1.2);
    pitsign.position.set(0, 5.2, 3.5); pit.add(pitsign);
    this.scene.add(pit);

    // grandstand
    const stand = new THREE.Group();
    stand.position.set(12, 0, 28);
    for (let i = 0; i < 4; i++){
      box(16, 1 + i * 0.6, 2.2, 0xC9C5BC, 0, 0.5 + i * 0.3, -i * 1.2, stand);
    }
    this.scene.add(stand);

    // fence posts around the apron
    for (let i = 0; i < 24; i++){
      const a = i / 24 * Math.PI * 2;
      const x = Math.cos(a) * 46;
      const z = Math.sin(a) * 46;
      cyl(0.12, 0.12, 2.4, C.rye, x, 1.2, z, this.scene, 6);
    }

    return spline;
  }

  _buildScenery(){
    // a few trees and shrubs scattered outside the track
    const spots = [
      [-34, 18], [-38, -8], [-30, -30], [32, -26], [38, 10], [26, 34]
    ];
    for (const [x, z] of spots){
      const g = new THREE.Group();
      g.position.set(x, 0, z);
      cyl(0.6, 0.5, 2.2, C.rye, 0, 1.1, 0, g, 8);
      const top = new THREE.Mesh(
        new THREE.ConeGeometry(2.4, 4.5, 8),
        new THREE.MeshLambertMaterial({ color: C.sprout })
      );
      top.position.y = 3.6; top.castShadow = true;
      g.add(top);
      this.scene.add(g);
    }
  }

  _makeCarMesh(color){
    const g = new THREE.Group();
    // body
    box(1.5, 0.5, 2.6, color, 0, 0.55, 0, g);
    // cabin
    box(1.1, 0.45, 1.3, 0xDDF3FF, 0, 0.98, -0.15, g);
    // wheels
    const wheelColor = 0x2A2A2A;
    let w = cyl(0.28, 0.28, 0.22, wheelColor, -0.82, 0.28,  0.85, g, 16); w.rotation.z = Math.PI/2;
    w = cyl(0.28, 0.28, 0.22, wheelColor,  0.82, 0.28,  0.85, g, 16); w.rotation.z = Math.PI/2;
    w = cyl(0.28, 0.28, 0.22, wheelColor, -0.82, 0.28, -0.85, g, 16); w.rotation.z = Math.PI/2;
    w = cyl(0.28, 0.28, 0.22, wheelColor,  0.82, 0.28, -0.85, g, 16); w.rotation.z = Math.PI/2;
    // tiny driver nub
    const driver = sphere(0.26, 0xFFF4DF, 0, 1.35, 0.2, g, 10);
    driver.scale.y = 1.3;
    return g;
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.steer = 0;
    this.input.brake = false;
    this._toExplore(true);
  }

  exit(){
    this._teardownRace();
    this._clearPanels();
    this.input.onTap = null;
    this.input.steer = 0;
    this.input.brake = false;
    HUD.showEnter(null); HUD.say('');
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._exploreTap(ray);
    HUD.setSubtitle('toast town speedway');
    if (first) HUD.say('Walk up to your car to race, or head back to the Garage.');
    HUD.setHint('click to walk · stand by your car to race');
    HUD.hideHintSoon(7000);
  }

  _exploreTap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    if (gp.length() > 44) gp.setLength(43.5);
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _startRace(){
    const equippedId = getEquippedCar() || 'toastmobile';
    const playerCar = getCarById(equippedId) || CARS[0];

    // pick three wholesome AI drivers
    const aiDrivers = spawnable()
      .filter(c => c.racer && c.wholesome)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    while (aiDrivers.length < 3){
      aiDrivers.push({ id: 'ryerocket', name: 'Rye Rocket', colors: { body: 0x8C6239 } });
    }

    // car roster for AI — one of each non-player car, or fallback
    const aiCarIds = CARS.filter(c => c.id !== playerCar.id).map(c => c.id);
    while (aiCarIds.length < 3) aiCarIds.push('toastmobile');

    const cars = [
      {
        id: 'player',
        name: 'Bread Boi',
        color: playerCar.color,
        isPlayer: true,
        startS: 0,
        stats: { topSpeed: playerCar.topSpeed, accel: playerCar.accel, grip: playerCar.grip }
      }
    ];
    for (let i = 0; i < 3; i++){
      const driver = aiDrivers[i];
      const car = getCarById(aiCarIds[i]) || CARS[0];
      cars.push({
        id: driver.id,
        name: driver.name,
        color: car.color,
        isPlayer: false,
        startS: (i + 1) * 0.015,
        stats: { topSpeed: car.topSpeed, accel: car.accel, grip: car.grip },
        driverNoise: { wobble: 0.04 + Math.random() * 0.06, wobbleSpeed: 1.5 + Math.random() * 2, brave: 0.30 + Math.random() * 0.25 }
      });
    }

    this.state = 'playing';
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.setHint(''); HUD.hideHint();
    HUD.say('');

    // build / recycle car meshes
    this.carMeshes.forEach(m => this.scene.remove(m));
    this.carMeshes = [];
    for (const c of cars){
      const mesh = this._makeCarMesh(c.color);
      mesh.visible = false; // will be placed by race update on first frame
      this.scene.add(mesh);
      this.carMeshes.push({ mesh, id: c.id });
    }

    this.race = new Race({ spline: this.spline, cars, input: this.input, onFinish: (r) => this._results(r) });
    this.race.mount();
    this.race.begin();
  }

  _results(r){
    this.state = 'results';
    this._teardownRace();
    this._clearPanels();
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    HUD.refreshCrumbs();

    const suffix = ['th','st','nd','rd'][r.placement] || 'th';
    const lap = r.bestLapMs ? this._formatMs(r.bestLapMs) : '—';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${r.placement}${suffix}</div>
        <h2>Grand Prix Finish!</h2>
        <div class="cafe-stats">
          <div><b>${this._formatMs(r.totalMs)}</b><span>total time</span></div>
          <div><b>${lap}</b><span>best lap</span></div>
          <div><b>${r.placement}${suffix}</b><span>placement</span></div>
          <div><b>${this.race?.state?.find(s => s.isPlayer)?.lap || LAPS}</b><span>laps</span></div>
        </div>
        <p class="cafe-payout">🍞 +${r.crumbs} crumbs${r.bonus ? ' <small>(best-lap bonus)</small>' : ''}</p>
        <button class="cafe-btn again">↻ race again</button>
        <button class="cafe-btn ghost leave">← back to the paddock</button>
      </div>`);
    p.querySelector('.again').onclick = () => this._startRace();
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _formatMs(ms){
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const cs = Math.floor((ms % 1000) / 10);
    return `${m}:${String(s).padStart(2,'0')}.${String(cs).padStart(2,'0')}`;
  }

  _teardownRace(){
    if (this.race){ this.race.unmount(); this.race = null; }
    this.carMeshes.forEach(m => this.scene.remove(m.mesh));
    this.carMeshes = [];
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  update(dt){
    if (this.state === 'playing' && this.race){
      this.race.update(dt);
      // sync meshes
      for (const rs of this.race.state){
        const entry = this.carMeshes.find(m => m.id === rs.id);
        if (entry){
          entry.mesh.visible = true;
          entry.mesh.position.copy(rs.pos);
          entry.mesh.position.y = 0;
          entry.mesh.rotation.y = rs.heading;
        }
      }
      return;
    }

    if (this.state === 'explore') this.bread.update(dt);

    let near = null, nd = Infinity;
    if (this.state === 'explore'){
      this.hotspots.forEach(h => {
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }
  }

  onResize(){
    const aspect = window.innerWidth / window.innerHeight;
    const viewSize = 62;
    this.camera.left = -viewSize * aspect / 2;
    this.camera.right = viewSize * aspect / 2;
    this.camera.top = viewSize / 2;
    this.camera.bottom = -viewSize / 2;
    this.camera.updateProjectionMatrix();
  }
}
